import React, { useEffect, useMemo, useState } from "react";
import { FileDown, FileSpreadsheet, RotateCcw } from "lucide-react";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import type { RelatorioConsumoLinha } from "../../../services/controleProducaoService";
import { BrazilDate } from "../../../utils/BrazilDate";
import {
  exportDetalhamentoPdf,
  exportDetalhamentoXlsx,
  type DetalhamentoFiltroResumo,
} from "./relatorioConsumoDetalheExport";
import { groupByUndNome, rotuloUnd } from "./groupByUnd";

function formatNum(n: number | null | undefined, und?: string): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const txt = Number.isInteger(n)
    ? n.toLocaleString("pt-BR")
    : n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
  return und ? `${txt} ${und}` : txt;
}

function formatBase(base: string | null | undefined, und?: string): string {
  const txt = base?.trim();
  if (!txt) return "—";
  return und ? `${txt} ${und}` : txt;
}

interface DetalhamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  linhas: RelatorioConsumoLinha[];
  lojaNome: string;
  periodoInicioISO: string;
  periodoFimISO: string;
}

const DetalhamentoModal: React.FC<DetalhamentoModalProps> = ({
  isOpen,
  onClose,
  linhas,
  lojaNome,
  periodoInicioISO,
  periodoFimISO,
}) => {
  const [filtroItemId, setFiltroItemId] = useState<number | "">("");
  const [filtroDiaSemana, setFiltroDiaSemana] = useState<number | "">("");
  const [filtroUnd, setFiltroUnd] = useState("");
  const [filtroDataInicio, setFiltroDataInicio] = useState("");
  const [filtroDataFim, setFiltroDataFim] = useState("");
  const [buscaItem, setBuscaItem] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setFiltroItemId("");
    setFiltroDiaSemana("");
    setFiltroUnd("");
    setFiltroDataInicio("");
    setFiltroDataFim("");
    setBuscaItem("");
  }, [isOpen, periodoInicioISO, periodoFimISO, linhas.length]);

  const itemOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const l of linhas) {
      if (!map.has(l.itemId)) map.set(l.itemId, l.descricao);
    }
    return Array.from(map.entries())
      .map(([id, descricao]) => ({ id, descricao }))
      .sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"));
  }, [linhas]);

  const undOptions = useMemo(
    () =>
      groupByUndNome(
        linhas.map((l) => ({ undNome: rotuloUnd(l.undNome) })),
        (x) => x.undNome
      ).map((g) => g.undNome),
    [linhas]
  );

  const diaSemanaOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const l of linhas) {
      if (l.diaSemana != null && l.diaSemanaLabel) {
        map.set(l.diaSemana, l.diaSemanaLabel);
      }
    }
    return Array.from(map.entries())
      .map(([valor, label]) => ({ valor, label }))
      .sort((a, b) => a.valor - b.valor);
  }, [linhas]);

  const linhasFiltradas = useMemo(() => {
    const busca = buscaItem.trim().toLowerCase();
    return linhas
      .filter((l) => {
        if (filtroItemId !== "" && l.itemId !== filtroItemId) return false;
        if (filtroDiaSemana !== "" && l.diaSemana !== filtroDiaSemana) {
          return false;
        }
        if (filtroUnd && rotuloUnd(l.undNome) !== filtroUnd) return false;
        const data = l.dataFicha ?? l.periodoKey;
        if (filtroDataInicio && data < filtroDataInicio) return false;
        if (filtroDataFim && data > filtroDataFim) return false;
        if (busca && !l.descricao.toLowerCase().includes(busca)) return false;
        return true;
      })
      .sort((a, b) => {
        const da = a.dataFicha ?? a.periodoKey;
        const db = b.dataFicha ?? b.periodoKey;
        if (da !== db) return da.localeCompare(db);
        return a.descricao.localeCompare(b.descricao, "pt-BR");
      });
  }, [
    linhas,
    filtroItemId,
    filtroDiaSemana,
    filtroUnd,
    filtroDataInicio,
    filtroDataFim,
    buscaItem,
  ]);

  const linhasPorUnd = useMemo(
    () => groupByUndNome(linhasFiltradas, (l) => l.undNome),
    [linhasFiltradas]
  );

  const filtrosResumo = useMemo((): DetalhamentoFiltroResumo[] => {
    const out: DetalhamentoFiltroResumo[] = [];
    if (filtroDataInicio || filtroDataFim) {
      const ini = filtroDataInicio
        ? BrazilDate.formatBR(BrazilDate.fromISOString(filtroDataInicio))
        : "—";
      const fim = filtroDataFim
        ? BrazilDate.formatBR(BrazilDate.fromISOString(filtroDataFim))
        : "—";
      out.push({ label: "Datas filtradas", value: `${ini} a ${fim}` });
    }
    if (filtroDiaSemana !== "") {
      const label =
        diaSemanaOptions.find((d) => d.valor === filtroDiaSemana)?.label ??
        String(filtroDiaSemana);
      out.push({ label: "Dia da semana", value: label });
    }
    if (filtroUnd) {
      out.push({ label: "Unidade", value: filtroUnd });
    }
    if (filtroItemId !== "") {
      const label =
        itemOptions.find((i) => i.id === filtroItemId)?.descricao ??
        String(filtroItemId);
      out.push({ label: "Item", value: label });
    }
    if (buscaItem.trim()) {
      out.push({ label: "Busca", value: buscaItem.trim() });
    }
    if (out.length === 0) {
      out.push({ label: "Filtros", value: "Todos os lançamentos do período" });
    }
    return out;
  }, [
    filtroDataInicio,
    filtroDataFim,
    filtroDiaSemana,
    filtroUnd,
    filtroItemId,
    buscaItem,
    diaSemanaOptions,
    itemOptions,
  ]);

  const buildExportInput = () => ({
    lojaNome,
    periodoInicioISO,
    periodoFimISO,
    filtros: filtrosResumo,
    linhas: linhasFiltradas,
    geradoEmLabel: BrazilDate.formatBR(new Date(), true),
  });

  const handleExportExcel = () => {
    if (linhasFiltradas.length === 0) {
      toast.warning("Nenhuma linha para exportar com os filtros atuais.");
      return;
    }
    try {
      exportDetalhamentoXlsx(buildExportInput());
      toast.success("Excel do detalhamento baixado.");
    } catch {
      toast.error("Não foi possível gerar o Excel.");
    }
  };

  const handleExportPdf = () => {
    if (linhasFiltradas.length === 0) {
      toast.warning("Nenhuma linha para exportar com os filtros atuais.");
      return;
    }
    try {
      exportDetalhamentoPdf(buildExportInput());
      toast.success("PDF do detalhamento baixado.");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  };

  const limparFiltros = () => {
    setFiltroItemId("");
    setFiltroDiaSemana("");
    setFiltroUnd("");
    setFiltroDataInicio("");
    setFiltroDataFim("");
    setBuscaItem("");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhamento completo"
      panelClassName="max-w-6xl w-full"
      bodyClassName="flex max-h-[85vh] flex-col p-4 sm:p-5"
      compactHeader
    >
      <p className="mb-4 text-sm text-neutral-600">
        Período do relatório:{" "}
        <span className="font-medium text-neutral-800">
          {BrazilDate.formatBR(BrazilDate.fromISOString(periodoInicioISO))} a{" "}
          {BrazilDate.formatBR(BrazilDate.fromISOString(periodoFimISO))}
        </span>
        {" · "}
        {lojaNome}
      </p>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm font-medium text-neutral-700">
          Data inicial
          <input
            type="date"
            value={filtroDataInicio}
            min={periodoInicioISO}
            max={filtroDataFim || periodoFimISO}
            onChange={(e) => setFiltroDataInicio(e.target.value)}
            className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
          />
        </label>
        <label className="text-sm font-medium text-neutral-700">
          Data final
          <input
            type="date"
            value={filtroDataFim}
            min={filtroDataInicio || periodoInicioISO}
            max={periodoFimISO}
            onChange={(e) => setFiltroDataFim(e.target.value)}
            className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
          />
        </label>
        <label className="text-sm font-medium text-neutral-700">
          Dia da semana
          <select
            value={filtroDiaSemana}
            onChange={(e) =>
              setFiltroDiaSemana(e.target.value ? Number(e.target.value) : "")
            }
            className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
          >
            <option value="">Todos</option>
            {diaSemanaOptions.map((dia) => (
              <option key={dia.valor} value={dia.valor}>
                {dia.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-neutral-700">
          Unidade de medida
          <select
            value={filtroUnd}
            onChange={(e) => setFiltroUnd(e.target.value)}
            className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
          >
            <option value="">Todas</option>
            {undOptions.map((und) => (
              <option key={und} value={und}>
                {und}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-neutral-700">
          Item
          <select
            value={filtroItemId}
            onChange={(e) =>
              setFiltroItemId(e.target.value ? Number(e.target.value) : "")
            }
            className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
          >
            <option value="">Todos</option>
            {itemOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.descricao}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium text-neutral-700 sm:col-span-2">
          Buscar item
          <input
            type="search"
            value={buscaItem}
            onChange={(e) => setBuscaItem(e.target.value)}
            placeholder="Ex.: Mussarela"
            className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2"
          />
        </label>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-neutral-600">
          <span className="font-semibold text-neutral-900">
            {linhasFiltradas.length.toLocaleString("pt-BR")}
          </span>{" "}
          linha(s) · {linhas.length.toLocaleString("pt-BR")} no total
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={limparFiltros}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <RotateCcw size={15} />
            Limpar filtros
          </button>
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={linhasFiltradas.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-600 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
          >
            <FileSpreadsheet size={16} />
            Excel
          </button>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={linhasFiltradas.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            <FileDown size={16} />
            PDF
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-neutral-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-neutral-50 shadow-sm">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Data</th>
              <th className="px-3 py-2 text-left font-semibold">Dia</th>
              <th className="px-3 py-2 text-left font-semibold">Item</th>
              <th className="px-3 py-2 text-right font-semibold">Base</th>
              <th className="px-3 py-2 text-right font-semibold">
                Est. inicial
              </th>
              <th className="px-3 py-2 text-right font-semibold">Produção</th>
              <th className="px-3 py-2 text-right font-semibold">Sobra</th>
              <th className="px-3 py-2 text-right font-semibold">Consumo</th>
            </tr>
          </thead>
          <tbody>
            {linhasFiltradas.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-10 text-center text-neutral-500"
                >
                  Nenhum lançamento com os filtros selecionados.
                </td>
              </tr>
            ) : (
              linhasPorUnd.map((grupo) => (
                <React.Fragment key={grupo.undNome}>
                  <tr className="border-t border-neutral-200 bg-neutral-100">
                    <td
                      colSpan={8}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-neutral-700"
                    >
                      Unidade {grupo.undNome}
                      <span className="ml-2 font-medium normal-case text-neutral-500">
                        {grupo.items.length}{" "}
                        {grupo.items.length === 1
                          ? "lançamento"
                          : "lançamentos"}
                      </span>
                    </td>
                  </tr>
                  {grupo.items.map((linha, idx) => (
                    <tr
                      key={`${linha.periodoKey}-${linha.itemId}-${idx}`}
                      className="border-t border-neutral-100"
                    >
                      <td className="whitespace-nowrap px-3 py-2">
                        {linha.periodoLabel}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {linha.diaSemanaLabel ?? "—"}
                      </td>
                      <td className="px-3 py-2">{linha.descricao}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatBase(linha.base, linha.undNome)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatNum(linha.estoqueInicial, linha.undNome)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatNum(linha.producao, linha.undNome)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatNum(linha.sobra, linha.undNome)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-primary">
                        {formatNum(linha.consumo, linha.undNome)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-neutral-200 bg-neutral-50">
                    <td
                      colSpan={5}
                      className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600"
                    >
                      Total {grupo.undNome}
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                      {formatNum(
                        grupo.items.reduce((a, l) => a + (l.producao ?? 0), 0),
                        grupo.undNome
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums">
                      {formatNum(
                        grupo.items.reduce((a, l) => a + (l.sobra ?? 0), 0),
                        grupo.undNome
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-primary">
                      {formatNum(
                        grupo.items.reduce((a, l) => a + (l.consumo ?? 0), 0),
                        grupo.undNome
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

export default DetalhamentoModal;
